import { Component } from '@theme/component';
import { debounce, isTouchDevice, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // Native mouseover fallback: the declarative on:pointerenter binding
    // misses activation on fast/diagonal cursor movement in some browsers.
    // A real mouseover listener (which bubbles and fires reliably regardless
    // of movement speed) covers that gap without replacing pointerenter.
    this.addEventListener('mouseover', this.#onMouseOver);
    // "Shop"/"Info" are real links (e.g. href="/collections/all") as a
    // sensible fallback destination, with the dropdown itself meant to be
    // the actual way in on top of that. On desktop that's fine — hovering
    // opens the dropdown before any click can land. Touch has no hover, so
    // a tap goes straight through to the link's own href, occasionally
    // navigating to that fallback collection instead of opening the
    // dropdown at all (inconsistent — sometimes the tap reads as opening
    // it, sometimes as following the link). On a touch device, tapping an
    // item that HAS a dropdown should only ever open/close it.
    //
    // Deliberately not just isTouchDevice() (which also gates
    // data-menu-style elsewhere) — that checks 'ontouchstart' in window,
    // which is a real, known-unreliable signal specifically on iPadOS
    // Safari (can read false on an actual iPad). maxTouchPoints alone is
    // the more robust modern signal for "this device can receive taps at
    // all", which is all this particular fix needs.
    if (isTouchDevice() || navigator.maxTouchPoints > 0) {
      this.addEventListener('click', this.#onTouchTriggerClick);
      // Closing on "tap outside" normally rides on #deactivate()'s own
      // relatedTarget/pointerleave checks, but pointerleave requires an
      // actual pointer to move off the element — a discrete tap somewhere
      // else on a touch device is a brand new touch sequence, not a "leave"
      // of the previously-active item, so nothing ever told it to close.
      // Listen on the document itself for exactly that: any tap that lands
      // outside this menu while something's open closes it.
      document.addEventListener('click', this.#onOutsideClick, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('click', this.#onTouchTriggerClick);
    document.removeEventListener('click', this.#onOutsideClick, true);
    this.#cleanupMutationObserver();
  }

  /**
   * Closes whatever's open when a tap lands outside this menu entirely —
   * see the connectedCallback comment above for why touch needs this as a
   * separate path from the hover-based pointerleave close.
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.#state.activeItem) return;
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * On touch devices, tapping a link that opens a dropdown (aria-haspopup)
   * should only toggle that dropdown, never follow the link's own href —
   * see the connectedCallback comment above for why that href exists at all.
   * A tap can synthesize a 'mouseover' first (inconsistently — that
   * inconsistency is the root cause this whole handler exists to work
   * around), which would already have opened it via the existing hover
   * path by the time 'click' fires; only treat the tap as a close if it
   * was already open before this tap's own possible mouseover, not the
   * split-second the mouseover from THIS SAME tap just opened it —
   * otherwise a tap that did synthesize mouseover would open-then-
   * immediately-close within the same gesture.
   * @param {MouseEvent} event
   */
  #onTouchTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[aria-haspopup="true"]');
    if (!trigger) return;

    event.preventDefault();

    // 300ms was meant to bridge only the same tap's own synthetic
    // mouseover→click gap, which browsers dispatch back-to-back within a
    // handful of milliseconds — not real time for a human to react. That
    // window was wide enough to also swallow a genuinely deliberate quick
    // second tap meant to close it, which read as the close not working
    // (or working only after several taps — "sluggish"). 80ms still
    // comfortably covers the synthetic-event gap without eating real taps.
    const openedJustNow = performance.now() - this.#lastActivatedAt < 80;
    if (trigger.getAttribute('aria-expanded') === 'true' && !openedJustNow) {
      this.#deactivate();
    } else {
      // activate() resolves its target via findMenuItem(), which expects
      // the *list item* (it does listItem.querySelector('[ref="menuitem"]')
      // internally) — not the link itself, which is what `trigger` is here.
      const listItem = trigger.closest('.menu-list__list-item');
      if (listItem) this.activate(/** @type {PointerEvent} */ ({ target: listItem }));
    }
  };

  /** @type {number} */
  #lastActivatedAt = 0;

  /**
   * Native mouseover fallback for menu-item activation.
   * @param {MouseEvent} event
   */
  #onMouseOver = (event) => {
    // On a device that can be tapped, opening is handled exclusively by
    // #onTouchTriggerClick — a real, deterministic click. Whether a tap
    // also synthesizes 'mouseover' first is inconsistent (that
    // inconsistency is the reason this whole touch path exists), and
    // letting both paths independently open/close the same item was
    // producing states where a second tap stopped closing it. Keep this
    // listener for actual mouse hover only.
    if (isTouchDevice() || navigator.maxTouchPoints > 0) return;

    if (!(event.target instanceof Element)) return;
    const listItem = event.target.closest('.menu-list__list-item');
    if (!listItem) return;
    // activate() itself no-ops if this item is already active, so no need
    // to dedupe here.
    this.activate(/** @type {PointerEvent} */ ({ target: listItem }));
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Rolling buffer of recent pointer Y positions, used to tell whether the
   * cursor's overall recent trajectory was upward or downward right as it
   * leaves a menu item. A single event's own `movementY` is too noisy for
   * this on real trackpads/mice — deceleration, diagonal movement, or a
   * slightly-off last sample can read as ~0 or flip sign right at the
   * boundary even when the shopper was clearly dragging upward, which made
   * the dropdown still close far too easily. Averaging over a short recent
   * window smooths that out.
   * @type {{ y: number, t: number }[]}
   */
  #pointerYHistory = [];

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const now = performance.now();
    this.#pointerYHistory.push({ y: event.clientY, t: now });
    while (this.#pointerYHistory.length > 1 && now - this.#pointerYHistory[0].t > 150) {
      this.#pointerYHistory.shift();
    }

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
    this.#pointerYHistory = [];
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    // The declarative on:pointerenter="/activate" binding (see the <li>
    // markup) fires for a touch tap too — pointerenter is a real
    // PointerEvent with pointerType 'touch' in that case, spec-compliant
    // and unlike the inconsistent synthetic 'mouseover'. Same reasoning as
    // #onMouseOver: on a device that can be tapped, only the deterministic
    // click handler (#onTouchTriggerClick) should open/close anything, so
    // this independent hover-ish path doesn't get to as well.
    if (event instanceof PointerEvent && event.pointerType === 'touch') return;

    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    this.#lastActivatedAt = performance.now();

    // "Shop"/"Info" open on hover via this mega-menu; the "Menu" dropdown
    // at tablet widths (.tablet-menu-dropdown, a plain <details>) is a
    // separate, independent component that doesn't know about this one —
    // opening one left the other sitting open on top of it. Close it here
    // so only one of the two is ever open at once.
    this.headerComponent.querySelectorAll('.tablet-menu-dropdown[open]').forEach((dropdown) => {
      /** @type {HTMLDetailsElement} */ (dropdown).open = false;
    });

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // Leaving the trigger while the pointer's recent trajectory was upward
    // shouldn't close its dropdown — only a downward exit should. This lets
    // a shopper drift back up toward the header row (reaching for
    // Cart/Search, or just passing back over "Shop"/"Info") without the
    // mega-menu vanishing underneath them; it stays open until they
    // actually move away downward, or hover/focus something else (which
    // still switches or closes it via activate()/blur, unaffected by this).
    // Compares against the oldest sample in the last ~150ms rather than the
    // leave event's own single movementY, which is too noisy on real
    // hardware to trust on its own (see #pointerYHistory).
    if (event instanceof PointerEvent && event.type === 'pointerleave') {
      const oldest = this.#pointerYHistory[0];
      if (oldest && event.clientY < oldest.y) {
        return;
      }
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
    // Also set on the root so elements outside #header-component (e.g. the
    // page-content scrim, which must live outside it to escape its
    // `contain: layout` and stay position:fixed to the viewport) can read it.
    document.documentElement.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
    // Drive the scrim's visibility from this same computed state, instead of
    // a separate CSS :hover selector — the header renders multiple copies of
    // the menu (desktop/drawer/navigation-bar variants) in the DOM at once,
    // and a hover match on a hidden duplicate could otherwise flip the scrim
    // on for the wrong reason (e.g. cursor near the bottom of the page).
    document.documentElement.toggleAttribute('data-mega-menu-open', !nothingToOpen);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
